"use client"

import { useEffect, useState } from "react"
import Link from "next/link"
import { useRouter } from "next/router"
import { Bookmark, Menu, Shapes, X } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Logo } from "@/components/generals"
import { useAuth } from "@/components/auth-provider"
import { useUserStatus } from "@/lib/use-user-status"
import { primaryNav } from "@/constants/site"
import { cn } from "@/lib/utils"

function CreditsMeter({ onNavigate }: { onNavigate?: () => void }) {
  const router = useRouter()
  const { status } = useUserStatus(true)

  if (!status) return null

  const unlimited = status.isSubscriber || status.isAdmin
  const remaining = Math.max(0, status.logoCredits)

  const label = unlimited
    ? "Unlimited"
    : remaining > 0
      ? `${remaining} paid ${remaining === 1 ? "logo" : "logos"} ready`
      : "$7 per logo"
  const description = unlimited
    ? "Unlimited generation is active on your plan."
    : remaining > 0
      ? "You have paid for a design that has not been used yet."
      : "Each logo is $7, paid before it is designed. See the plans for unlimited."

  return (
    <button
      type="button"
      onClick={() => {
        onNavigate?.()
        router.push("/pricing")
      }}
      title={description}
      className={cn(
        "inline-flex h-8 items-center gap-2 rounded-full border px-3 text-caption font-medium transition-colors duration-150 ease-out",
        remaining === 0 && !unlimited
          ? "border-line bg-surface-2 text-ink-muted hover:border-ink hover:text-ink"
          : "border-line bg-surface text-ink-muted hover:border-ink hover:text-ink"
      )}
    >
      <span
        aria-hidden
        className={cn(
          "size-1.5 rounded-full",
          unlimited ? "bg-success" : remaining > 0 ? "bg-signal" : "bg-ink-subtle"
        )}
      />
      {label}
    </button>
  )
}

export default function Navbar() {
  const router = useRouter()
  const pathname = router.asPath
  const { user, loading, isAdmin, isEditor, signOut } = useAuth()
  const [menuOpen, setMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  const loginUrl = `/login?returnTo=${encodeURIComponent(pathname || "/")}`

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8)
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  // Close the mobile menu on navigation.
  useEffect(() => {
    setMenuOpen(false)
  }, [router.asPath])

  // Lock the page behind the open menu, and let Escape close it.
  useEffect(() => {
    if (!menuOpen) return

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false)
    }

    const previous = document.body.style.overflow
    document.body.style.overflow = "hidden"
    document.addEventListener("keydown", onKey)

    return () => {
      document.body.style.overflow = previous
      document.removeEventListener("keydown", onKey)
    }
  }, [menuOpen])

  const isActive = (href: string) =>
    href === "/" ? pathname === "/" : pathname.startsWith(href)

  return (
    <header
      className={cn(
        "sticky top-0 z-50 w-full bg-paper/85 backdrop-blur-md transition-[border-color] duration-200 ease-out",
        scrolled ? "border-b border-line" : "border-b border-transparent"
      )}
    >
      <div className="container flex h-16 items-center justify-between gap-4">
        <div className="flex items-center gap-8">
          <Link
            href="/"
            className="flex items-center gap-2 rounded-sm"
            aria-label="Modulify AI Logo Maker, home"
          >
            <Logo priority />
          </Link>

          <nav aria-label="Main" className="hidden items-center gap-7 lg:flex">
            {primaryNav.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                aria-current={isActive(item.href) ? "page" : undefined}
                className={cn(
                  "text-small transition-colors duration-150 ease-out",
                  isActive(item.href)
                    ? "font-medium text-ink"
                    : "text-ink-muted hover:text-ink"
                )}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-2">
          {!loading && user && <CreditsMeter />}

          {!loading && user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="inline-flex h-11 items-center gap-2 rounded-full border border-line bg-surface pl-4 pr-1 text-small text-ink transition-colors duration-150 ease-out hover:border-line-strong max-sm:h-9 max-sm:border-transparent max-sm:bg-transparent max-sm:p-0"
                  aria-label="Account menu"
                >
                  <span className="max-w-[9rem] truncate font-medium max-sm:hidden">
                    {user.name || "Your account"}
                  </span>
                  <Avatar className="size-9 border border-line">
                    <AvatarImage src={user.photoURL || ""} alt="" />
                    <AvatarFallback className="bg-surface-2 text-caption text-ink-muted">
                      {user.name?.charAt(0) || user.email?.charAt(0) || "U"}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>

              <DropdownMenuContent
                align="end"
                sideOffset={10}
                className={cn(
                  "w-[min(240px,100vw-32px)] overflow-hidden rounded-[14px] border-0 bg-[#000000b5] p-0 text-white shadow-[0_30px_70px_-24px_#0000008c] backdrop-blur-[10px] duration-[180ms] ease-out",
                  "[&_[role=menuitem]]:cursor-pointer [&_[role=menuitem]]:gap-3 [&_[role=menuitem]]:rounded-none [&_[role=menuitem]]:px-4 [&_[role=menuitem]]:py-2.5 [&_[role=menuitem]]:text-small [&_[role=menuitem]]:text-white/70",
                  "[&_[role=menuitem]:focus]:bg-white/10 [&_[role=menuitem]:focus]:text-white"
                )}
              >
                {/* Identity */}
                <div className="flex flex-col gap-3.5 p-4">
                  {(isAdmin || isEditor) && (
                    <span className="inline-flex w-fit items-center rounded-full bg-white px-2.5 py-0.5 type-eyebrow text-ink">
                      {isAdmin ? "Admin" : "Editor"}
                    </span>
                  )}

                  <div className="flex items-center gap-3">
                    <Avatar className="size-10 rounded-md border border-white/15">
                      <AvatarImage src={user.photoURL || ""} alt="" className="rounded-md" />
                      <AvatarFallback className="rounded-md bg-white/10 text-small text-white">
                        {user.name?.charAt(0) || user.email?.charAt(0) || "U"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex min-w-0 flex-col">
                      <p className="truncate font-display text-small font-medium text-white">
                        {user.name || "Your account"}
                      </p>
                      <p className="truncate text-caption text-white/55">{user.email}</p>
                    </div>
                  </div>

                  <Link
                    href="/account"
                    className="inline-flex h-9 w-full items-center justify-center rounded-full bg-white/10 text-small font-medium text-white transition-colors duration-150 ease-out hover:bg-white/20"
                  >
                    Account
                  </Link>
                </div>

                <DropdownMenuSeparator className="mx-0 my-0 bg-white/10" />

                <DropdownMenuItem asChild>
                  <Link href="/mylogo">
                    <Bookmark aria-hidden /> My logos
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link href="/my-image-to-vector">
                    <Shapes aria-hidden /> My conversions
                  </Link>
                </DropdownMenuItem>

                {(isAdmin || isEditor) && (
                  <>
                    <DropdownMenuSeparator className="mx-0 my-0 bg-white/10" />
                    {isAdmin && (
                      <>
                        <DropdownMenuItem asChild>
                          <Link href="/admin-settings">Admin settings</Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link href="/admin/users">User management</Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link href="/admin/payment-verification">Payment verification</Link>
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuItem asChild>
                      <Link href="/admin/blog">Blog management</Link>
                    </DropdownMenuItem>
                    {isAdmin && (
                      <>
                        <DropdownMenuItem asChild>
                          <Link href="/image-generator">Image generator</Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link href="/billing">Billing</Link>
                        </DropdownMenuItem>
                      </>
                    )}
                  </>
                )}

                <DropdownMenuSeparator className="mx-0 my-0 bg-white/10" />
                <DropdownMenuItem onClick={signOut}>Log out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {!loading && !user && (
            <Button asChild size="sm" className="hidden rounded-full px-5 sm:inline-flex">
              <Link href={loginUrl}>Sign in</Link>
            </Button>
          )}

          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            className="inline-flex size-9 items-center justify-center rounded-md text-ink transition-colors duration-150 ease-out hover:bg-surface-2 lg:hidden"
          >
            {menuOpen ? <X className="size-5" aria-hidden /> : <Menu className="size-5" aria-hidden />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div
          id="mobile-menu"
          className="animate-reveal-fade border-t border-line bg-paper lg:hidden"
        >
          <div className="container flex flex-col py-4">
            <nav aria-label="Mobile" className="flex flex-col">
              {primaryNav.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  aria-current={isActive(item.href) ? "page" : undefined}
                  className={cn(
                    "flex items-center justify-between border-b border-line py-3.5 font-display text-h4",
                    isActive(item.href) ? "text-ink" : "text-ink-muted"
                  )}
                >
                  {item.label}
                </Link>
              ))}
              {user && (
                <Link
                  href="/mylogo"
                  className="flex items-center justify-between border-b border-line py-3.5 font-display text-h4 text-ink-muted"
                >
                  My logos
                </Link>
              )}
            </nav>

            <div className="flex items-center gap-3 pt-5">
              {!loading && !user && (
                <Button asChild className="flex-1 rounded-full">
                  <Link href={loginUrl}>Sign in</Link>
                </Button>
              )}
              {!loading && user && (
                <Button asChild variant="outline" className="flex-1 rounded-full">
                  <Link href="/account">Account</Link>
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  )
}
